import React, { useRef } from 'react';
import { useSignMessage } from 'wagmi';
import { gql } from '@soundxyz/gql-string';
import { useStableCallback } from '@soundxyz/graphql-react-query/utils';
import {
  ERROR_TYPE,
  MINTING_ERROR_ACTIONS,
  MINTING_INFO_ACTIONS,
  PILLARS,
} from '@soundxyz/utils/constants/logging';
import { useSafeLayoutEffect } from '@components-shared/src/hooks/useSafeLayoutEffect';
import { styled } from '@components-shared/src/stitches';
import { Button } from '@components/main-components/button/Button';
import { TextArea } from '@components/main-components/forms/inputs/TextArea/TextArea';
import { Modal } from '@components/main-components/modals/Modal';
import { useConnectedAuth } from '@context/auth/connected';
import { MediaQuery } from '@context/mediaQuery';
import { ModalContext, ModalType } from '@context/modal';
import { ToastContext } from '@context/toast';
import { RefetchOperationsOnComplete } from '@graphql/helpers/effects';
import {
  NftInitialCommentDocument,
  ReleasePageDocument,
  SetCommentDocument,
  setQueryData,
  useMutation,
  useQuery,
} from '@graphql/reactQuery';
import { useConnectedWallet } from '@hooks/useConnectedWallet';
import { useLogError } from '@hooks/useLogErrorNew';
import { useLogInfo } from '@hooks/useLogInfo';
import { useNftOwnershipData } from '@hooks/useNftOwnershipData';
import { COMMENT_MAX_CHARS } from '@utils/constants';
import { DataTestId } from '@utils/test';
import { ShareToSocialModal } from './ShareToSocial';
import { StatusBar } from './StatusBar';
import {
  ButtonWrapper,
  containerStyles,
  Content,
  headerStyles,
  InstructionHeader,
  InstructionHelper,
  ModalWrapper,
} from './styles';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation SetComment($nftId: UUID!, $message: String!, $signature: String!) {
    setComment(nftId: $nftId, message: $message, signature: $signature) {
      id
      comment {
        id
        message
      }
      releaseCollector: collectorRelease {
        id
      }
      release {
        id
        ...ShareToSocialModalReleaseInfo
      }
      ...ShareToSocialModalNftInfo
    }
  }

  query NftInitialComment($nftId: UUID!) {
    nftFromId(id: $nftId) {
      id
      comment {
        id
        message
      }
    }
  }
`);

const pillar = PILLARS.MINTING;
const isArtistOnlyPage = false;

export function SetCommentModal({ releaseId, nftId }: { releaseId: string; nftId?: string }) {
  const { tablet } = MediaQuery.useContainer();
  const { openModal, closeCancelModal } = ModalContext.useContainer();
  const { openToast, closeToast } = ToastContext.useContainer();
  const { signMessageAsync } = useSignMessage();
  const { getDeepLink, isAuthenticating } = useConnectedAuth();
  const logError = useLogError();
  const logInfo = useLogInfo();
  const { nftOwnedByUser, currentComment } = useNftOwnershipData({ releaseId });

  const didWrite = useRef(false);

  const { data: nftInitialCommentData, isInitialLoading } = useQuery(NftInitialCommentDocument, {
    variables: !!nftId && { nftId },
    staleTime: 0,
  });

  const { mutateAsync: setComment, isLoading: commentMutationLoading } = useMutation(
    SetCommentDocument,
    {
      retry: 5,
      onSuccess({
        data: {
          setComment: { id, comment },
        },
      }) {
        setQueryData(
          {
            query: NftInitialCommentDocument,
            variables: { nftId: id },
          },
          {
            data: {
              nftFromId: {
                id,
                comment,
              },
            },
          },
        );
      },
    },
  );

  const nftInitialCommentDataMessage = nftId
    ? nftInitialCommentData?.data.nftFromId?.comment?.message ||
      (nftOwnedByUser?.id === nftId ? currentComment : undefined)
    : undefined;

  const [commentText, setCommentText] = React.useState(nftInitialCommentDataMessage || '');

  useSafeLayoutEffect(() => {
    if (!nftInitialCommentDataMessage || didWrite.current) return;

    setCommentText(nftInitialCommentDataMessage);
  }, [nftInitialCommentDataMessage]);

  const nftToEditId = nftId || nftOwnedByUser?.id;

  const handleSetComment = useStableCallback(async () => {
    if (!nftToEditId) return;

    if (commentText.trim().length === 0) {
      openToast({
        text: 'Comments cannot be blank. Please try again.',
        variant: 'error',
      });
      return;
    }

    let signature: string | undefined;
    let deepLink: string | null = null;
    try {
      openToast({
        text: 'Please approve the signature request in your wallet.',
      });
      /**
       * This is critical for a better mobile UX.
       * We have to set before the actual transaction is sent.
       * This way it can open the user's wallet app and make it a seamless flow
       */
      deepLink = getDeepLink();
      if (deepLink) {
        window.location.href = deepLink;
      }
      signature = await signMessageAsync({
        message: commentText,
      });
    } catch (err) {
      logError({
        action: MINTING_ERROR_ACTIONS.SET_COMMENT_ERROR,
        error: err,
        errorType: ERROR_TYPE.SIGN_MESSAGE_ERROR,
        message: 'Error signing message',
        level: 'fatal',
        pillar,
        toast: 'Something went wrong. Please try again.',
        isArtistOnlyPage,
        indexedTags: {
          releaseId,
          nftId,
        },
        unindexedExtra: {
          deepLink, // this helps us know if users was going through the mobile deep linking flow or not
          commentText,
        },
      });

      return;
    }

    if (!signature) {
      const message = 'No signature';
      logError({
        action: MINTING_ERROR_ACTIONS.SET_COMMENT_ERROR,
        error: new Error(message),
        errorType: ERROR_TYPE.MISSING_SIGNATURE,
        message: 'Error signing message',
        level: 'fatal',
        pillar,
        toast: 'Something went wrong. Please try again.',
        isArtistOnlyPage,
        indexedTags: {
          releaseId,
          nftId,
        },
        unindexedExtra: {
          deepLink,
          commentText,
        },
      });
      return;
    }

    openToast({
      text: <p className="loading-ellipsis">Setting your comment</p>,
    });

    try {
      const response = await setComment(
        {
          signature,
          nftId: nftToEditId,
          message: commentText,
        },
        {},
      );
      if (!!response.data?.setComment) {
        openModal(ModalType.SHARE_TO_SOCIAL, {
          body: <ShareToSocialModal releaseId={releaseId} nftId={nftToEditId} />,
        });
        logInfo({
          action: MINTING_INFO_ACTIONS.COMMENT_SET,
          message: `User set comment on NFT ${nftId}`,
          pillar,
          isArtistOnlyPage,
          data: {
            nftId: nftToEditId,
            commentText,
            releaseId,
          },
        });
      } else {
        openToast({
          text: 'Something went wrong. Please try again',
          variant: 'error',
        });
      }
      closeToast();
    } catch (err) {
      closeToast();
      logError({
        action: MINTING_ERROR_ACTIONS.SET_COMMENT_ERROR,
        error: err,
        errorType: ERROR_TYPE.UNKNOWN,
        message: 'Error signing message',
        level: 'fatal',
        pillar,
        toast: 'Encountered an error. Please try again.',
        isArtistOnlyPage,
        indexedTags: {
          releaseId,
          nftId,
        },
        unindexedExtra: {
          commentText,
        },
      });
    }
  });

  const handleClick = useConnectedWallet({
    onAuth: handleSetComment,
  });

  const commentErr = commentText.length > COMMENT_MAX_CHARS;

  const body = (
    <ModalWrapper>
      <StatusBar releaseId={releaseId} />

      <Content>
        <InstructionHeader>Leave comment</InstructionHeader>
        <InstructionHelper>Tell everyone what you think about this song.</InstructionHelper>
        <Textarea
          data-testid={DataTestId.SET_COMMENT_MODAL_TESTAREA}
          placeholder={isInitialLoading ? '...' : 'What would you like to comment...'}
          onChange={e => {
            didWrite.current = true;
            setCommentText(e.target.value);
          }}
          hasError={!!commentErr}
          errorMsg={'Exceeds maximum character limit of ' + COMMENT_MAX_CHARS}
          value={commentText}
          disabled={isInitialLoading}
        />
      </Content>
      <ButtonWrapper>
        <Button
          fullWidth={!tablet}
          data-testid={DataTestId.SET_COMMENT_MODAL_NEXT_BUTTON}
          label={commentMutationLoading ? 'Posting...' : 'Next'}
          variant="primary"
          loading={commentMutationLoading || isAuthenticating}
          disabled={commentMutationLoading || commentErr || isAuthenticating}
          onClick={handleClick}
        />
      </ButtonWrapper>
    </ModalWrapper>
  );

  return (
    <Modal
      headerText="Leave Comment"
      body={body}
      closeModal={closeCancelModal}
      containerStyles={containerStyles}
      headerStyles={headerStyles}
    />
  );
}

RefetchOperationsOnComplete({
  trigger: [SetCommentDocument],
  refetch: [ReleasePageDocument],
});

const Textarea = styled(TextArea, {
  width: 'calc(100vw - 64px)',

  '@tablet': {
    display: 'flex',
    justifyContent: 'flex-end',
    width: 577,
  },
});
